import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import type { ApprovalView } from '../../types/e2i';
import { formatDateTime } from '../../utils/e2i';

const STATE_TEXT: Record<string, string> = {
  Done: ': approved',
  Current: ': waiting',
  Waiting: '',
  Skipped: ': not needed',
};

/** A quotation's or a job's approval stages (the module's own stages; one approval per stage). */
const ApprovalStages: React.FC<{ approval: ApprovalView | null | undefined; emptyText?: string }> = ({ approval, emptyText }) => {
  const stages = approval?.stages || [];
  if (!stages.length) {
    return <Typography variant="body2" color="text.secondary">{emptyText || 'No approval stage applies.'}</Typography>;
  }
  return (
    <Stack component="ol" direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ m: 0, p: 0, listStyle: 'none' }} flexWrap="wrap" useFlexGap>
      {stages.map((s) => (
        <Stack component="li" key={s.stepNumber} direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '1px',
              ...(s.status === 'Done'
                ? { bgcolor: '#1e6b2a', color: '#fff' }
                : s.status === 'Current'
                  ? { border: '2px solid #E8B640', bgcolor: '#fdf3cf' }
                  : s.status === 'Skipped'
                    ? { border: '1px dashed #9aa6ad' }
                    : { border: '1px solid #9aa6ad' }),
            }}
          >
            {s.status === 'Done' ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={s.status === 'Current' ? 600 : 400}>
              {s.name} (stage {s.stepNumber})
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{STATE_TEXT[s.status] || ''}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {s.role ? `Role ${s.role}` : ''}
              {s.approvers.length
                ? `${s.role ? ' · ' : ''}${s.approvers
                  .map((a) => `${a.name}${a.status === 'Approved' ? ` ✓${a.decidedAt ? ` ${formatDateTime(a.decidedAt)}` : ''}` : a.status === 'Skipped' ? ' (not needed)' : ''}`)
                  .join(', ')}`
                : s.status === 'Waiting' ? `${s.role ? ' · ' : ''}not reached yet` : s.status === 'Current' ? `${s.role ? ' · ' : ''}no approver found` : ''}
              {s.approvers.length > 1 ? ' · any one approves' : ''}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default ApprovalStages;
